import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, LogIn, Lock, User, AlertCircle, FileText, PenLine, BarChart3, ShieldCheck } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password) {
      setError('Por favor introduce tu usuario y contraseña');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await login(form.username, form.password);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Error al iniciar sesión. Verifica tus credenciales.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* ───────────── PANEL IZQUIERDO — Marca ───────────── */}
      <div className="login-left" style={{
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 60%, #2a0d1f 100%)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '60px 50px'
      }}>
        {/* Glow rosa decorativo */}
        <div style={{
          position: 'absolute',
          top: '-20%',
          right: '-15%',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(233,30,140,0.18) 0%, rgba(233,30,140,0) 70%)',
          pointerEvents: 'none'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-10%',
          left: '-10%',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(233,30,140,0.12) 0%, rgba(233,30,140,0) 70%)',
          pointerEvents: 'none'
        }} />

        {/* TOP: Sello de empresa pequeño */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 14px',
            border: '1px solid rgba(233,30,140,0.3)',
            borderRadius: 999,
            fontSize: '0.7rem',
            color: 'rgba(255,255,255,0.7)',
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
            fontWeight: 600
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#e91e8c', boxShadow: '0 0 8px #e91e8c' }} />
            Cumplimiento AEMPS · RD 192/2023
          </div>
        </div>

        {/* CENTRO: Marca con texto elegante */}
        <div style={{ position: 'relative', zIndex: 1, textAlign: 'left' }}>
          {/* Marca: texto "Lomhifar" con acento rosa decorativo */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 18,
            marginBottom: 24
          }}>
            {/* Acento decorativo rosa al lado izquierdo */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4
            }}>
              <div style={{ display: 'flex', gap: 4 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'rgba(233,30,140,0.4)' }} />
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#e91e8c', boxShadow: '0 0 12px rgba(233,30,140,0.8)' }} />
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'rgba(233,30,140,0.5)' }} />
              </div>
              <div style={{ display: 'flex', gap: 4, marginLeft: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#e91e8c', boxShadow: '0 0 10px rgba(233,30,140,0.7)' }} />
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'rgba(233,30,140,0.6)' }} />
              </div>
              <div style={{ display: 'flex', gap: 4, marginLeft: 2 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'rgba(233,30,140,0.35)' }} />
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#e91e8c', boxShadow: '0 0 12px rgba(233,30,140,0.7)' }} />
              </div>
            </div>

            <div style={{
              fontFamily: '"Segoe UI", "Helvetica Neue", Arial, sans-serif',
              fontSize: '3.8rem',
              fontWeight: 300,
              letterSpacing: '-0.02em',
              color: '#f5f5f5',
              lineHeight: 1
            }}>
              Lomh<span style={{ position: 'relative' }}>
                i
                <span style={{
                  position: 'absolute',
                  top: '0.18em',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  background: '#e91e8c',
                  boxShadow: '0 0 12px rgba(233,30,140,0.7)'
                }} />
              </span>far
            </div>
          </div>

          <div style={{
            width: 60,
            height: 3,
            background: 'linear-gradient(90deg, #e91e8c 0%, transparent 100%)',
            borderRadius: 2,
            marginBottom: 22
          }} />

          <div style={{
            color: 'rgba(255,255,255,0.85)',
            fontSize: '1.3rem',
            fontWeight: 300,
            lineHeight: 1.4,
            letterSpacing: '0.3px',
            maxWidth: 420
          }}>
            Distribución farmacéutica<br />
            <span style={{ color: '#e91e8c', fontWeight: 600 }}>especializada</span> y certificada.
          </div>

          <div style={{
            color: 'rgba(255,255,255,0.45)',
            fontSize: '0.85rem',
            marginTop: 14,
            maxWidth: 420,
            lineHeight: 1.6
          }}>
            Sistema interno de gestión documental para procedimientos
            normalizados de trabajo (PNT) y productos sanitarios.
          </div>
        </div>

        {/* BOTTOM: características en iconos */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 14,
            maxWidth: 480
          }}>
            {[
              [FileText, 'Gestión de PNTs y procedimientos'],
              [PenLine, 'Firma electrónica certificada'],
              [BarChart3, 'Informes para inspección sanitaria'],
              [ShieldCheck, 'Acceso seguro con roles'],
            ].map(([Icon, text]) => (
              <div key={text} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                color: 'rgba(255,255,255,0.65)',
                fontSize: '0.82rem'
              }}>
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: 'rgba(233,30,140,0.12)',
                  border: '1px solid rgba(233,30,140,0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Icon size={15} color="#e91e8c" />
                </div>
                {text}
              </div>
            ))}
          </div>

          <div style={{
            marginTop: 32,
            paddingTop: 20,
            borderTop: '1px solid rgba(255,255,255,0.08)',
            fontSize: '0.72rem',
            color: 'rgba(255,255,255,0.35)',
            letterSpacing: '0.5px'
          }}>
            © {new Date().getFullYear()} LOMHIFAR · Todos los derechos reservados
          </div>
        </div>
      </div>

      {/* ───────────── PANEL DERECHO — Formulario ───────────── */}
      <div className="login-right">
        <div className="login-form-box">
          <div style={{ marginBottom: 36 }}>
            <div style={{
              width: 52, height: 52, background: 'var(--color-primary)',
              borderRadius: 14, display: 'flex', alignItems: 'center',
              justifyContent: 'center', marginBottom: 22,
              boxShadow: '0 8px 20px rgba(233,30,140,0.25)'
            }}>
              <Lock size={22} color="white" />
            </div>
            <h1 style={{ fontSize: '1.55rem', marginBottom: 6, fontWeight: 700 }}>Iniciar Sesión</h1>
            <p style={{ fontSize: '0.875rem', color: 'var(--color-text-3)' }}>
              Accede con tu usuario y contraseña
            </p>
          </div>

          {error && (
            <div className="alert alert-danger" style={{ marginBottom: 20 }}>
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Usuario o Email</label>
              <div style={{ position: 'relative' }}>
                <User size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-3)' }} />
                <input
                  className="form-control"
                  style={{ paddingLeft: 38 }}
                  type="text"
                  placeholder="Tu nombre de usuario"
                  value={form.username}
                  onChange={e => setForm({ ...form, username: e.target.value })}
                  autoFocus
                  autoComplete="username"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Contraseña</label>
              <div style={{ position: 'relative' }}>
                <Lock size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-3)' }} />
                <input
                  className="form-control"
                  style={{ paddingLeft: 38, paddingRight: 40 }}
                  type={showPass ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-3)' }}
                  onClick={() => setShowPass(!showPass)}
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-lg"
              style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}
              disabled={loading}
            >
              {loading ? <div className="spinner" style={{ width: 18, height: 18 }} /> : <LogIn size={18} />}
              {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </button>
          </form>

          <div style={{
            marginTop: 28,
            padding: '14px 16px',
            background: 'var(--color-gray-1)',
            borderRadius: 8,
            fontSize: '0.78rem',
            color: 'var(--color-text-3)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            lineHeight: 1.5
          }}>
            <Lock size={14} style={{ marginTop: 2, flexShrink: 0, color: 'var(--color-text-3)' }} />
            <div>
              <strong style={{ color: 'var(--color-text-2)' }}>¿Has olvidado tu contraseña?</strong><br />
              Contacta con el administrador del sistema para restablecerla.
            </div>
          </div>

          <div style={{ marginTop: 24, textAlign: 'center', fontSize: '0.72rem', color: 'var(--color-text-3)' }}>
            LOMHIFAR · Sistema de Gestión Documental de Productos Sanitarios
          </div>
        </div>
      </div>
    </div>
  );
}
